<template>
  <div class="buy-ticket-wrapper">
    <div class="buy-steps">
      <div class="buy-step done">1. 车次信息</div>
      <div class="buy-step active">2. 填写订单</div>
      <div class="buy-step">3. 网上支付</div>
      <div class="buy-step">4. 购票成功</div>
    </div>
    <Space direction="vertical" :style="{ width: '100%' }" size="large">
      <Card>
        <template #title>
          <div class="title-wrapper">
            <h1>车次信息</h1>
            <h2>（以下余票信息仅供参考）</h2>
            <Tag v-if="state.isReschedule" color="orange" style="margin-left: 12px">
              改签
            </Tag>
          </div>
        </template>
        <div>
          <span class="important-text">{{ query.departureDate }}</span>
          <span class="important-text"
            >（{{ getWeekNumber(dayjs(query.departureDate).day()) }}）</span
          >
          <span class="important-text">{{ query.trainNumber }}</span>
          <span class="small-text">次</span>
          <span class="important-text">{{ state.currTrain?.departure }}</span>
          <span class="small-text">站</span>
          <span class="important-text"
            >（{{ state.currTrain?.departureTime }}开）——</span
          >
          <span class="important-text">{{ state.currTrain?.arrival }}</span>
          <span class="small-text">站</span>
          <span class="important-text"
            >（{{ state.currTrain?.duration }}到）</span
          >
        </div>
        <Divider dashed></Divider>
        <div class="seat-wrapper">
          <div v-for="item in state.currentSeat">
            <span>{{
              SEAT_CLASS_TYPE_LIST.find((seat) => seat.code === item.type)
                ?.label
            }}</span>
            （ <span class="price">￥{{ item?.price }}</span
            >）
            <span>{{ item.quantity >= 1 ? '有票' : '1张票' }}</span>
          </div>
        </div>
        <div class="tip">
          *显示的卧铺票价均为上铺票价，供您参考；具体票价以您确认支付时实际购买的席别票价为准。
        </div>
      </Card>
      <Card>
        <template #title>
          <div class="title-wrapper">
            <h1>乘车人信息</h1>
            <h2>（请准确填写乘车人身份证件信息）</h2>
            <Tag v-if="state.isReschedule" color="orange" style="margin-left: 12px">
              改签
            </Tag>
          </div>
        </template>
        <div class="user-tip">
          <UserOutlined />
          <span>乘车人</span>
        </div>
        <div class="check-wrapper">
          <div class="passenger-operator">
            <Select
              v-model:value="currPassenger"
              mode="multiple"
              :options="
                state.currPassengerList.map((item) => ({
                  value: item.id,
                  label: `${item.realName}（${item.idCard}）`
                }))
              "
              :max-tag-count="4"
              :max-tag-placeholder="(omittedValues) => `+${omittedValues.length}人`"
              show-search
              option-filter-prop="label"
              :placeholder="
                state.isReschedule
                  ? '请选择原订单乘车人（仅支持原订单乘客改签）'
                  : '输入姓名或证件号搜索并选择乘车人'
              "
              :style="{
                width: state.isReschedule ? '100%' : 'calc(100% - 110px)'
              }"
            />
            <Button
              v-if="!state.isReschedule"
              type="primary"
              ghost
              @click="state.addPassengerOpen = true"
            >
              新增乘车人
            </Button>
          </div>
        </div>
        <div
          v-if="state.isReschedule && state.originalOrder"
          class="origin-order-summary"
        >
          <span class="origin-label">原订单车次：</span>
          <span class="origin-value">
            {{ state.originalOrder.ridingDate }}
            {{ state.originalOrder.trainNumber }}次
            {{ state.originalOrder.departure }}站（{{
              state.originalOrder.departureTime
            }}开）— {{ state.originalOrder.arrival }}站（{{
              state.originalOrder.arrivalTime
            }}到）
          </span>
        </div>
        <Divider></Divider>
        <Table
          :columns="tableColumns"
          :data-source="
            (state.dataSource ?? []).filter((item) =>
              currPassenger?.includes(item.id)
            )
          "
          :locale="{ emptyText: '请从上方选择乘车人' }"
          :pagination="false"
        >
          <template #originTicket="{ record }">
            <div class="origin-ticket-cell">
              <div>
                <span class="cell-label">乘客类型</span>
                {{ formatOriginTicketType(record) }}
              </div>
              <div>
                <span class="cell-label">席别</span>
                {{ formatOriginSeatType(record) }}
              </div>
              <div>
                <span class="cell-label">席位</span>
                {{ formatOriginSeatPosition(record) }}
              </div>
              <div v-if="record?.originAmount != null">
                <span class="cell-label">票价</span>
                <span class="origin-price"
                  >¥{{ (record.originAmount / 100).toFixed(0) }}</span
                >
              </div>
            </div>
          </template>
          <template #ticketType="{ text, record }">
            <Select
              :style="styleWidth"
              :value="text"
              @select="(value) => handleChooseTicketType(record.id, value)"
            >
              <SelectOption
                v-for="item in TICKET_TYPE_LIST"
                :value="item.value"
                >{{ item.label }}</SelectOption
              >
            </Select>
          </template>
          <template #seatType="{ text, record }">
            <Select
              :style="styleWidth"
              :value="record.seatType"
              @select="(value) => handleChooseSeat(record.id, value)"
            >
              <SelectOption
                v-for="item in state.currentSeat"
                :value="item.type"
              >
                {{
                  `${
                    SEAT_CLASS_TYPE_LIST.find((seat) => seat.code === item.type)
                      ?.label
                  }(￥${item.price})`
                }}</SelectOption
              >
            </Select>
          </template>
          <template #realName="{ text }">
            <Input :value="text" disabled :style="styleWidth"></Input>
          </template>
          <template #idType="{ text }">
            <Select disabled :style="styleWidth" :value="text">
              <SelectOption v-for="item in ID_CARD_TYPE" :value="item.value">{{
                item.label
              }}</SelectOption>
            </Select>
          </template>
          <template #idCard="{ text }">
            <Input disabled :value="text" :style="styleWidth"></Input>
          </template>
          <template #edit="{ text, record }">
            <CloseCircleOutlined
              :style="{ color: '#fd6a09' }"
              @click="() => handleDelete(record.id)"
            />
          </template>
        </Table>
        <img
          :style="{ width: '100%' }"
          src="https://kyfw.12306.cn/otn/resources/images/ins_ad7.png"
          alt=""
        />
      </Card>
      <div>
        <span>提交订单表示已阅读并同意</span>
        <span
          ><a href="javascript:void(0)">《中国铁路旅客运输规程》</a
          ><a href="javascript:void(0)">《铁路12306服务条款》</a></span
        >
      </div>
      <div :style="{ width: '100%', textAlign: 'center' }">
        <Space size="large">
          <Button @click="() => router.push('/ticketSearch')">返回重新查询</Button>
          <Button
            class="submit-btn"
            @click="
              () => {
                handleSubmit()
              }
            "
            >提交订单</Button
          >
        </Space>
      </div>
      <div class="tips-txt">
        <h2>温馨提示：</h2>
        <p>
          1.
          一张有效身份证件同一乘车日期同一车次只能购买一张车票，高铁动卧列车除外。
        </p>
        <p>
          2.
          购买儿童票时，乘车儿童有有效身份证件的，请填写本人有效身份证件信息。自2023年1月1日起，每一名持票成年人旅客可免费携带一名未满6周岁且不单独占用席位的儿童乘车，超过一名时，超过人数应购买儿童优惠票。免费儿童可以在购票成功后添加。
        </p>
        <p>
          3.
          购买残疾军人（伤残警察）优待票的，须在购票后、开车前办理换票手续方可进站乘车。换票时，不符合规定的减价优待条件，没有有效"中华人民共和国残疾军人证"或"中华人民共和国伤残人民警察证"的，不予换票，所购车票按规定办理退票手续。
        </p>
        <p>
          4.
          一天内3次申请车票成功后取消订单（包含无座票时取消5次计为取消1次），当日将不能在12306继续购票。
        </p>
        <p>
          <strong
            >5.
            购买铁路乘意险的注册用户年龄须在18周岁以上，使用非中国居民身份证注册的用户如购买铁路乘意险，须在<a
              href="../view/information.html"
              shape="rect"
              >我的12306——个人信息</a
            >
            如实填写“出生日期”。</strong
          >
        </p>
        <p>
          <strong
            >6.
            父母为未成年子女投保，须在<a
              href="../view/passengers.html"
              shape="rect"
              >我的乘车人</a
            >
            登记未成年子女的有效身份证件信息。</strong
          >
        </p>
        <p>7.
          未尽事宜详见《铁路旅客运输规程》等有关规定和车站公告。</p>
        <p name="xjky" style="display: none">
          8.
          为确保乘客在旅途中有一个安全、舒适的乘坐环境，自2020年11月17日起，<span
            style="color: red"
            >旅客不得随身携带长宽高之和大于130厘米的雪具乘车</span
          >
          。您可选择雪具快运服务，请提前1-2天选择雪具快运“门到站”或“站到站”服务，中铁快运提供雪具到站后3日免费保管，请您根据出行时间，提前咨询和办理。中铁快运客服热线：95572<br
            clear="none"
          />
        </p>
      </div>
    </Space>
    <Modal
      :visible="state.addPassengerOpen"
      title="新增乘车人"
      :confirm-loading="state.addPassengerLoading"
      @ok="handleAddPassenger"
      @cancel="state.addPassengerOpen = false"
      ok-text="保存并选中"
      cancel-text="取消"
    >
      <Form :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
        <FormItem label="姓名" required>
          <Input v-model:value="addPassengerForm.realName" placeholder="请输入姓名" />
        </FormItem>
        <FormItem label="证件类型">
          <Select v-model:value="addPassengerForm.idType">
            <SelectOption :value="0">身份证</SelectOption>
          </Select>
        </FormItem>
        <FormItem label="证件号码" required>
          <Input v-model:value="addPassengerForm.idCard" placeholder="请输入证件号码" />
        </FormItem>
        <FormItem label="手机号" required>
          <Input v-model:value="addPassengerForm.phone" placeholder="请输入手机号" />
        </FormItem>
        <FormItem label="乘客类型">
          <Select v-model:value="addPassengerForm.discountType">
            <SelectOption v-for="item in DISCOUNTS_TYPE" :key="item.value" :value="item.value">
              {{ item.label }}
            </SelectOption>
          </Select>
        </FormItem>
      </Form>
    </Modal>
    <Modal
      :visible="state.open"
      title="请核对乘车人证件信息及车票信息"
      wrap-class-name="check-info-wrapper"
      :width="920"
      @cancel="state.open = false"
      :footer="null"
    >
      <Space direction="vertical" size="small" :style="{ width: '100%' }">
        <div>
          <span class="important-text">{{ query.departureDate }}</span>
          <span class="important-text"
            >（{{ getWeekNumber(dayjs(query.departureDate).day()) }}）</span
          >
          <span class="important-text">{{ query.trainNumber }}</span>
          <span class="small-text">次</span>
          <span class="important-text">{{ state.currTrain?.departure }}</span>
          <span class="small-text">站</span>
          <span class="important-text"
            >（{{ state.currTrain?.departureTime }}开）——</span
          >
          <span class="important-text">{{ state.currTrain?.arrival }}</span>
          <span class="small-text">站</span>
          <span class="important-text"
            >（{{ state.currTrain?.duration }}到）</span
          >
        </div>
        <Table
          :columns="checkColumns"
          :data-source="state.dataSource"
          :pagination="false"
        >
          <template #seatType="{ text, record }">
            <span>
              {{
                SEAT_CLASS_TYPE_LIST.find((seat) => seat.code === text)?.label
              }}
            </span>
          </template>
          <template #discountType="{ text }">
            {{ TICKET_TYPE_LIST.find((item) => item.value === text)?.label }}
          </template>
          <template #idType="{ text }">
            {{ ID_CARD_TYPE.find((item) => item.value === text)?.label }}
          </template>
        </Table>
        <!-- 高铁/动车：可视化选座 -->
        <div v-if="showSeatDiagram" class="seat-diagram-block">
          <a class="seat-disclaimer" href=""
            >*如果本次列车剩余席位无法满足您的选座需求，系统将自动为您分配席位</a
          >
          <div class="seat-choose-wrapper">
            <div class="seat-choose-head">
              <div class="tip seat-tip">
                <IconFont type="icon-laba001"></IconFont>
                在线选座
              </div>
              <div class="seat-count-text">
                已选座{{ state.currentSeatCode.length }}/{{
                  state.dataSource.length
                }}
              </div>
            </div>
            <div class="seat-rows">
              <div v-for="(item, index) in state.dataSource?.slice(0, 2)" :key="index">
                <div class="action-wrapper">
                  <div>窗</div>
                  <Divider type="vertical"></Divider>
                  <div>
                    <div
                      class="seat-img"
                      v-for="(item, index, length) in state.seatPosition
                        .slice(
                          0 + index * state.seatNumber,
                          (1 + index) * state.seatNumber
                        )
                        .slice(0, state.seatLeft)"
                      @click="() => handleSelectSeat(item)"
                      :class="{
                        cur: state.currentSeatCode?.indexOf(item) !== -1
                      }"
                    >
                      {{ item.slice(0, 1) }}
                    </div>
                    <Divider type="vertical"></Divider>
                  </div>
                  <div>过道</div>
                  <Divider type="vertical"></Divider>
                  <div>
                    <div
                      class="seat-img"
                      v-for="(item, index, length) in state.seatPosition
                        .slice(
                          0 + index * state.seatNumber,
                          (1 + index) * state.seatNumber
                        )
                        .slice(state.seatLeft, state.seatNumber)"
                      @click="() => handleSelectSeat(item)"
                      :class="{
                        cur: state.currentSeatCode?.indexOf(item) !== -1
                      }"
                    >
                      {{ item.slice(0, 1) }}
                    </div>
                    <Divider type="vertical"></Divider>
                  </div>
                  <div>窗</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- 硬卧/软卧等：选铺步进器（对齐 12306 交互） -->
        <div v-else-if="showBerthPicker" class="berth-picker-block">
          <div class="berth-disclaimer">
            *如剩余席位（铺位）无法满足您的需求，系统将自动为您分配。
          </div>
          <div class="berth-choose-wrapper">
            <div class="berth-choose-head">
              <div class="tip berth-tip">
                <IconFont type="icon-laba001"></IconFont>
                在线选铺
              </div>
              <div class="berth-count-text">
                已选铺 {{ berthTotalSelected }}/{{ state.dataSource.length }}
              </div>
            </div>
            <div class="berth-steppers">
              <div class="berth-stepper-item">
                <span class="berth-label">下铺</span>
                <div class="berth-controls">
                  <Button
                    shape="circle"
                    size="small"
                    class="berth-step-btn"
                    :disabled="state.berthLower <= 0"
                    @click="adjustBerth('berthLower', -1)"
                  >
                    <MinusOutlined />
                  </Button>
                  <span class="berth-num">{{ state.berthLower }}</span>
                  <Button
                    shape="circle"
                    size="small"
                    type="primary"
                    ghost
                    class="berth-step-btn"
                    :disabled="!canIncreaseBerth"
                    @click="adjustBerth('berthLower', 1)"
                  >
                    <PlusOutlined />
                  </Button>
                </div>
              </div>
              <div v-if="showThreeLevelBerth" class="berth-stepper-item">
                <span class="berth-label">中铺</span>
                <div class="berth-controls">
                  <Button
                    shape="circle"
                    size="small"
                    class="berth-step-btn"
                    :disabled="state.berthMiddle <= 0"
                    @click="adjustBerth('berthMiddle', -1)"
                  >
                    <MinusOutlined />
                  </Button>
                  <span class="berth-num">{{ state.berthMiddle }}</span>
                  <Button
                    shape="circle"
                    size="small"
                    type="primary"
                    ghost
                    class="berth-step-btn"
                    :disabled="!canIncreaseBerth"
                    @click="adjustBerth('berthMiddle', 1)"
                  >
                    <PlusOutlined />
                  </Button>
                </div>
              </div>
              <div class="berth-stepper-item">
                <span class="berth-label">上铺</span>
                <div class="berth-controls">
                  <Button
                    shape="circle"
                    size="small"
                    class="berth-step-btn"
                    :disabled="state.berthUpper <= 0"
                    @click="adjustBerth('berthUpper', -1)"
                  >
                    <MinusOutlined />
                  </Button>
                  <span class="berth-num">{{ state.berthUpper }}</span>
                  <Button
                    shape="circle"
                    size="small"
                    type="primary"
                    ghost
                    class="berth-step-btn"
                    :disabled="!canIncreaseBerth"
                    @click="adjustBerth('berthUpper', 1)"
                  >
                    <PlusOutlined />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else>
          <a href="">*系统将随机为您申请席位，暂不支持自选席位</a>
        </div>
        <Divider dashed></Divider>
        <div class="info-tip">
          本次列车，<span v-for="item in state.currentSeat">
            {{
              SEAT_CLASS_TYPE_LIST.find((seat) => seat.code === item.type)
                ?.label
            }}余票{{ item.quantity }},</span
          >
        </div>
        <Space
          size="middle"
          :style="{
            width: '100%',
            textAlign: 'center',
            justifyContent: 'center'
          }"
        >
          <Button @click="state.open = false">返回修改</Button>
          <Button
            :loading="state.loading"
            :style="{
              backgroundColor: '#ff8001',
              color: '#fff',
              border: 'none'
            }"
            @click="handleSubmitBuyTicket"
            >确认</Button
          >
        </Space>
      </Space>
    </Modal>
  </div>
</template>

<script setup>
import {
  Card,
  Divider,
  Space,
  Table,
  Select,
  SelectOption,
  Input,
  Button,
  Modal,
  message,
  Form,
  FormItem,
  Tag
} from 'ant-design-vue'
import { useRoute } from 'vue-router'
import {
  fetchTicketSearch,
  fetchPassengerList,
  fetchBuyTicket,
  fetchAddPassenger,
  fetchRescheduleTicket,
  fetchOrderBySn
} from '@/service'
import { onMounted, reactive, toRaw, watch, ref, computed } from 'vue'
import { getWeekNumber } from '@/utils'
import {
  UserOutlined,
  CloseCircleOutlined,
  PlusOutlined,
  MinusOutlined
} from '@ant-design/icons-vue'
import { TICKET_TYPE_LIST, ID_CARD_TYPE, DISCOUNTS_TYPE } from '@/constants'
import IconFont from '@/components/icon-font'
import dayjs from 'dayjs'
import { SEAT_CLASS_TYPE_LIST } from '@/constants'
import Cookie from 'js-cookie'
import router from '@/router'
import { saveReschedulePending } from '@/utils/rescheduleStorage'
import { formatSeatSecondLine } from '@/utils/orderDisplay'
const styleWidth = { width: '150px' }

const { query } = useRoute()
const username = Cookie.get('username')
const state = reactive({
  /** 改签相关 */
  isReschedule: false,
  orderSn: null,
  originalOrder: null,
  /** 其他状态 */
  currTrain: null,
  currPassengerList: [],
  dataSource: [],
  rawDataSource: [],
  currentSeat: [],
  quoteTimestamp: null,
  open: false,
  currentSeatCode: [],
  seatPosition: [],
  isChooseSeat: true,
  seatLeft: 3,
  seatNumber: 5,
  loading: false,
  addPassengerOpen: false,
  addPassengerLoading: false,
  /** 选铺：下/中/上铺人数偏好（与乘车人数之和可为 0 表示不限） */
  berthLower: 0,
  berthMiddle: 0,
  berthUpper: 0
})

/** 支持选铺的席别编码：硬卧、二等卧、软卧、一等卧、高级软卧、动卧 */
const BERTH_SEAT_TYPES = new Set([7, 5, 6, 4, 9, 10])
/** 开放下/中/上三档的席别 */
const THREE_LEVEL_BERTH_TYPES = new Set([7, 5])

const primarySeatType = computed(() => state.dataSource[0]?.seatType)

const showBerthPicker = computed(
  () =>
    !!state.dataSource?.length && BERTH_SEAT_TYPES.has(primarySeatType.value)
)

const showThreeLevelBerth = computed(() =>
  THREE_LEVEL_BERTH_TYPES.has(primarySeatType.value)
)

const showSeatDiagram = computed(() => {
  if (showBerthPicker.value) return false
  if (!state.isChooseSeat) return false
  if (state.dataSource?.length > 5) return false
  return state.dataSource?.length <= state.seatPosition.length
})

const berthTotalSelected = computed(
  () => state.berthLower + state.berthMiddle + state.berthUpper
)

const canIncreaseBerth = computed(
  () => berthTotalSelected.value < state.dataSource.length
)

const adjustBerth = (key, delta) => {
  const cur = state[key]
  if (delta < 0 && cur <= 0) return
  const next = berthTotalSelected.value + delta
  if (delta > 0 && next > state.dataSource.length) return
  state[key] = cur + delta
}
const currPassenger = ref([])
const addPassengerForm = reactive({
  realName: '',
  idType: 0,
  discountType: 0,
  phone: '',
  idCard: ''
})

onMounted(() => {
  // 检查是否为改签流程
  if (query.isReschedule === '1' && query.orderSn) {
    state.isReschedule = true
    state.orderSn = query.orderSn
    message.info('您正在进行车票改签操作，请为原乘车人选择新车次')
  }

  fetchTicketSearch({
    fromStation: query.fromStation,
    toStation: query.toStation,
    departureDate: query.departureDate,
    trainNo: query.trainNo || query.trainNumber
  }).then((res) => {
    if (res.success) {
      const trainNo = query.trainNo || query.trainNumber
      const currTrain =
        res.data.trainList?.find((item) => item.trainNumber === trainNo) ??
        res.data.trainList?.[0]
      if (!currTrain) {
        message.error('未查询到该车次信息，请返回重新搜索')
        return
      }
      state.currentSeat = currTrain.seatClassList
      state.currTrain = currTrain
      state.quoteTimestamp = res.data.timestamp ?? query.quoteTimestamp ?? null
    }
  })
  loadPassengerList()
})

/** 按姓名+证件号匹配原订单乘车人与账户乘车人 */
const matchPassengerKey = (realName, idCard) =>
  `${(realName || '').trim()}::${(idCard || '').trim()}`

/** 完整证件号与脱敏证件号（前4+后4）是否同一人 */
const isSameIdCard = (cardA, cardB) => {
  const a = (cardA || '').trim().toUpperCase()
  const b = (cardB || '').trim().toUpperCase()
  if (!a || !b) return false
  if (a === b) return true
  const matchMaskedWithFull = (masked, full) => {
    if (!masked.includes('*') || full.includes('*') || full.length < 8) return false
    return full.startsWith(masked.slice(0, 4)) && full.endsWith(masked.slice(-4))
  }
  if (a.includes('*')) return matchMaskedWithFull(a, b)
  if (b.includes('*')) return matchMaskedWithFull(b, a)
  return false
}

const isSamePassenger = (orderPassenger, userPassenger) => {
  if (
    orderPassenger.realName &&
    userPassenger.realName &&
    orderPassenger.realName !== userPassenger.realName
  ) {
    return false
  }
  const orderCard = orderPassenger.idCard || ''
  const userCard = userPassenger.idCard || ''
  const userActualCard = userPassenger.actualIdCard || ''
  const idCardMatched =
    orderCard &&
    (orderCard === userCard ||
      orderCard === userActualCard ||
      isSameIdCard(orderCard, userCard) ||
      isSameIdCard(orderCard, userActualCard))
  if (idCardMatched) {
    return true
  }
  return (
    matchPassengerKey(orderPassenger.realName, orderCard) ===
    matchPassengerKey(userPassenger.realName, userCard)
  )
}

/**
 * 原订单：已出票(3)/改签失败(23) 可改签；已改签(50) 不可在原单再选
 * 改签子单(22)：对改签票再改签，当日已办则不可（看 updateTime）
 */
const canRescheduleOrderPassenger = (orderP, orderHeaderStatus) => {
  const status = orderP.status
  const header = orderHeaderStatus ?? state.originalOrder?.status
  if (header === 22) {
    if (status !== 22 && status !== 3) return false
    if (!orderP.updateTime) return true
    return !dayjs(orderP.updateTime).isSame(dayjs(), 'day')
  }
  // 原单/部分退票(11)/部分改签(34)/部分退改(38)：已出票(3)/改签失败(23)；改签中(21)、已退、已改签不可选
  if (status === 21 || status === 40 || status === 41 || status === 50) return false
  return status === 3 || status === 23
}

/** 改签：按订单类型过滤可改签乘车人 */
const filterPassengersByOrder = (allPassengers, orderPassengers, orderHeaderStatus) => {
  const matched = []
  for (const orderP of orderPassengers) {
    if (!canRescheduleOrderPassenger(orderP, orderHeaderStatus)) continue
    const userP = allPassengers.find((p) => isSamePassenger(orderP, p))
    if (userP) {
      matched.push({
        ...userP,
        discountType: orderP.ticketType ?? userP.discountType,
        originTicketType: orderP.ticketType,
        originSeatType: orderP.seatType,
        originCarriageNumber: orderP.carriageNumber,
        originSeatNumber: orderP.seatNumber,
        originAmount: orderP.amount,
        orderItemStatus: orderP.status
      })
    }
  }
  return matched
}

/** 车次余票列表中第一个席别（与上方车次信息区顺序一致） */
const getDefaultSeatType = () => {
  const seats = state.currentSeat ?? []
  if (!seats.length) return undefined
  const firstAvailable = seats.find(
    (s) => s.quantity == null || Number(s.quantity) > 0
  )
  return (firstAvailable ?? seats[0])?.type
}

const applyPassengerList = (list, selectedId) => {
  state.currPassengerList = list
  state.dataSource = list.map((item, index) => ({
    ...item,
    keyNumber: index + 1
  }))
  state.rawDataSource = list.map((item, index) => ({
    ...item,
    keyNumber: index + 1
  }))
  if (selectedId) {
    currPassenger.value = [...new Set([...currPassenger.value, selectedId])]
  }
}

const loadPassengerList = async (selectedId) => {
  const passengerRes = await fetchPassengerList({ username })
  if (!passengerRes.success) return

  const allPassengers = passengerRes.data ?? []

  if (state.isReschedule && state.orderSn) {
    const orderRes = await fetchOrderBySn({ orderSn: state.orderSn })
    if (!orderRes.success || !orderRes.data?.passengerDetails?.length) {
      message.warning('未能加载原订单乘车人，请返回订单列表重试')
      applyPassengerList([], selectedId)
      return
    }
    state.originalOrder = orderRes.data
    const orderPassengers = orderRes.data.passengerDetails
    const headerStatus = orderRes.data.status
    const filtered = filterPassengersByOrder(
      allPassengers,
      orderPassengers,
      headerStatus
    )
    if (!filtered.length) {
      if (headerStatus === 22) {
        message.warning('今日已办理改签，请次日再对改签单发起改签')
      } else if (orderPassengers.every((p) => p.status === 50)) {
        message.warning('原订单乘车人均已改签，请至对应「改签票」订单办理再次改签')
      } else if (orderPassengers.every((p) => p.status === 40)) {
        message.warning('订单乘车人均已退票，无法改签')
      } else if (
        (headerStatus === 11 || headerStatus === 38) &&
        !orderPassengers.some((p) => p.status === 3 || p.status === 23)
      ) {
        message.warning('部分退票订单中暂无可改签乘客（仅剩余未退票乘客可改签）')
      } else {
        message.warning('暂无可改签乘客（仅支持未退票且已出票/改签失败的乘客，或乘车人信息未匹配）')
      }
    }
    applyPassengerList(filtered, selectedId)
    return
  }

  applyPassengerList(allPassengers, selectedId)
}

watch(
  () => state.dataSource,
  (newValue) => {
    let seatType = newValue?.length && newValue[0]?.seatType
    let seatPosition = ['A', 'B', 'C', 'D', 'E']
    let seatLeft = 3
    if (
      seatType ===
      SEAT_CLASS_TYPE_LIST.find((item) => item.label === '商务座')?.code
    ) {
      seatPosition = ['A', 'C', 'F']
      seatLeft = 2
    }
    if (
      seatType ===
      SEAT_CLASS_TYPE_LIST.find((item) => item.label === '一等座')?.code
    ) {
      seatPosition = ['A', 'C', 'D', 'F']
      seatLeft = 2
    }
    if (
      seatType ===
      SEAT_CLASS_TYPE_LIST.find((item) => item.label === '二等座')?.code
    ) {
      seatPosition = ['A', 'B', 'C', 'D', 'F']
      seatLeft = 3
    }
    console.log('newValue.length:::', newValue?.length)
    console.log('seatPosition?.length :::', seatPosition?.length)
    let seatList = new Array(
      (newValue?.length ?? 1) * (seatPosition?.length ?? 1)
    ).fill('')

    seatList = seatList?.map((item, index) => {
      if (index < seatPosition?.length - 1) {
        return `${seatPosition[index]}0`
      } else {
        return `${seatPosition[index % seatPosition.length]}${Math.floor(
          index / seatPosition?.length
        )}`
      }
    })
    state.seatPosition = seatList
    state.seatLeft = seatLeft
    state.seatNumber = seatPosition?.length
  },
  {
    deep: true
  }
)

watch(
  () => currPassenger.value,
  (newValue) => {
    const list = (newValue ?? []).map((item) => toRaw(item))
    const defaultSeatType = getDefaultSeatType()
    const unifiedSeatType =
      state.dataSource.find((item) => item.seatType != null)?.seatType ??
      defaultSeatType
    // 按多选框勾选顺序排列；序号始终为当前表格行号 1、2、3…（不用乘车人列表里的全局位置）
    const ordered = []
    let seq = 1
    for (const id of list) {
      const row = state.rawDataSource.find((item) => item.id === id)
      const existing = state.dataSource.find((item) => item.id === id)
      if (row) {
        const copy = { ...row, keyNumber: seq++ }
        if (existing?.seatType != null) {
          copy.seatType = existing.seatType
        } else if (
          (copy.seatType === null || copy.seatType === undefined) &&
          unifiedSeatType != null
        ) {
          copy.seatType = unifiedSeatType
        }
        ordered.push(copy)
      }
    }
    state.dataSource = ordered
  },
  { deep: true }
)

watch(
  () => state.dataSource,
  (newValue) => {
    let isChooseSeat = true
    let seatType = newValue?.length && newValue[0].seatType
    newValue?.length &&
      newValue.map((item) => {
        if (item.seatType !== seatType) {
          isChooseSeat = false
        }
      })
    state.isChooseSeat = isChooseSeat
  },
  { deep: true }
)

const formatOriginTicketType = (record) =>
  TICKET_TYPE_LIST.find((item) => item.value === record?.originTicketType)
    ?.label ?? '—'

const formatOriginSeatType = (record) =>
  SEAT_CLASS_TYPE_LIST.find((seat) => seat.code === record?.originSeatType)
    ?.label ?? '—'

const formatOriginSeatPosition = (record) => {
  const line = formatSeatSecondLine({
    carriageNumber: record?.originCarriageNumber,
    seatNumber: record?.originSeatNumber,
    seatType: record?.originSeatType
  })
  return line || '—'
}

const baseColumns = [
  {
    title: '序号',
    dataIndex: 'keyNumber'
  },
  {
    title: '乘客类型',
    dataIndex: 'discountType',
    slots: { customRender: 'ticketType' }
  },
  {
    title: '席别',
    dataIndex: '',
    slots: { customRender: 'seatType' }
  },
  {
    title: '姓名',
    dataIndex: 'realName',
    slots: { customRender: 'realName' }
  },
  {
    title: '证件类型',
    dataIndex: 'idType',
    slots: { customRender: 'idType' }
  },
  {
    title: '证件号码',
    dataIndex: 'idCard',
    slots: { customRender: 'idCard' }
  },
  {
    title: '操作',
    dataIndex: '',
    slots: { customRender: 'edit' }
  }
]

const tableColumns = computed(() => {
  if (!state.isReschedule) return baseColumns
  const cols = [...baseColumns]
  const seatCol = cols.find((c) => c.slots?.customRender === 'seatType')
  if (seatCol) seatCol.title = '新席别'
  cols.splice(1, 0, {
    title: '原票信息',
    key: 'originTicket',
    width: 200,
    slots: { customRender: 'originTicket' }
  })
  return cols
})

const handleDelete = (id) => {
  state.dataSource = (state.dataSource ?? []).filter((item) => {
    return item.id !== id
  })
  currPassenger.value = currPassenger.value.filter((item) => {
    return item !== id
  })
}

const handleChooseSeat = (_id, value) => {
  state.dataSource = state.dataSource.map((item) => ({
    ...item,
    seatType: value
  }))
}

const handleChooseTicketType = (id, value) => {
  let cIndex
  state.dataSource.find((item, index) => {
    if (item.id === id) {
      cIndex = index
      return true
    }
  })
  state.dataSource[cIndex]['discountType'] = value
}

const handleAddPassenger = () => {
  if (
    !addPassengerForm.realName ||
    !addPassengerForm.idCard ||
    !addPassengerForm.phone
  ) {
    return message.error('请补全新增乘车人信息')
  }
  state.addPassengerLoading = true
  fetchAddPassenger({ username, ...addPassengerForm })
    .then((res) => {
      if (res.success) {
        message.success('新增乘车人成功')
        state.addPassengerOpen = false
        addPassengerForm.realName = ''
        addPassengerForm.idCard = ''
        addPassengerForm.phone = ''
        addPassengerForm.idType = 0
        addPassengerForm.discountType = 0
        loadPassengerList(res.data?.id)
      } else {
        message.error(res.message || '新增失败')
      }
    })
    .finally(() => {
      state.addPassengerLoading = false
    })
}

/**
 * 改签席别校验：
 * 1. 所选乘车人原票席别须一致（如均为二等座）
 * 2. 所选乘车人本次改签席别须一致（如均为一等座，可与原票不同）
 */
const validateRescheduleSeatRules = () => {
  const selected = state.dataSource ?? []
  if (!selected.length) {
    message.error('请选择需要改签的乘车人')
    return false
  }

  const missingSeat = selected.some(
    (item) => item.seatType === null || item.seatType === undefined
  )
  if (missingSeat) {
    message.error('请为每位乘车人选择席别')
    return false
  }

  const withOrigin = selected.filter(
    (item) => item.originSeatType !== null && item.originSeatType !== undefined
  )
  if (withOrigin.length > 0) {
    const firstOrigin = withOrigin[0].originSeatType
    const mixedOrigin = withOrigin.some(
      (item) => item.originSeatType !== firstOrigin
    )
    if (mixedOrigin) {
      message.error(
        '改签时，所选乘车人原票席别须一致，请勿混合不同席别乘客一并改签'
      )
      return false
    }
  }

  const newSeatTypes = [...new Set(selected.map((item) => item.seatType))]
  if (newSeatTypes.length > 1) {
    message.error('改签时，所选乘车人本次席别须保持一致')
    return false
  }

  return true
}

const handleSubmit = () => {
  const canNotSubmit = state.dataSource.some(
    (item) => item.seatType === null || item.seatType === undefined
  )
  if (canNotSubmit || state.dataSource?.length === 0) {
    return message.error('请添加乘车人，并为每位乘车人选择席别')
  }
  if (state.isReschedule && !validateRescheduleSeatRules()) {
    return
  }
  state.berthLower = 0
  state.berthMiddle = 0
  state.berthUpper = 0
  state.open = true
}

const checkColumns = [
  {
    title: '序号',
    dataIndex: 'keyNumber'
  },
  {
    title: '席别',
    dataIndex: 'seatType',
    slots: { customRender: 'seatType' }
  },
  {
    title: '乘客类型',
    dataIndex: 'discountType',
    slots: { customRender: 'discountType' }
  },
  {
    title: '姓名',
    dataIndex: 'realName'
  },
  {
    title: '证件类型',
    dataIndex: 'idType',
    slots: { customRender: 'idType' }
  },
  {
    title: '证件号码',
    dataIndex: 'idCard'
  }
]

const handleSelectSeat = (code) => {
  if (state.currentSeatCode?.indexOf(code) !== -1) {
    state.currentSeatCode = state.currentSeatCode?.filter(
      (item) => item !== code
    )
  } else {
    if (state.currentSeatCode.length === state.dataSource.length) {
      state.currentSeatCode[state.currentSeatCode.length - 1] = code
    } else {
      state.currentSeatCode.push(code)
    }
  }
}

const handleSubmitBuyTicket = () => {
  const n = state.dataSource.length
  const berthSum = berthTotalSelected.value
  if (showBerthPicker.value && berthSum > 0 && berthSum !== n) {
    message.error(
      `所选铺位总数须等于乘车人数（${n} 人），或不选铺由系统自动分配`
    )
    return
  }
  if (state.isReschedule && !validateRescheduleSeatRules()) {
    return
  }
  let params = { trainId: query?.trainId }
  const passengers = state.dataSource.map((item) => ({
    passengerId: item.id,
    seatType: item.seatType
  }))
  const chooseBerths =
    showBerthPicker.value && berthSum > 0
      ? {
          lower: state.berthLower,
          middle: showThreeLevelBerth.value ? state.berthMiddle : 0,
          upper: state.berthUpper
        }
      : undefined
  params = {
    ...params,
    passengers,
    chooseSeats: showBerthPicker.value ? [] : toRaw(state.currentSeatCode),
    chooseBerths,
    departure: state.currTrain?.departure,
    arrival: state.currTrain?.arrival,
    ticketDate: query?.departureDate
  }

  state.loading = true

  // 改签：进入确认页，由「立即改签」再调后端
  if (state.isReschedule && state.orderSn) {
    state.loading = false
    state.open = false
    params.orderSn = state.orderSn
    params.username = Cookie.get('username')
    params.userId = Cookie.get('userId')
    const seatType = state.dataSource[0]?.seatType
    const seatInfo = state.currentSeat?.find((s) => s.type === seatType)
    params.quoteSnapshot = {
      timestamp: state.quoteTimestamp || query.quoteTimestamp || null,
      deliveryId: seatInfo?.deliveryId || null
    }
    saveReschedulePending({
      apiParams: params,
      trainInfo: {
        departureDate: query?.departureDate,
        trainNumber: query?.trainNumber ?? state.currTrain?.trainNumber,
        departure: state.currTrain?.departure,
        arrival: state.currTrain?.arrival,
        departureTime: state.currTrain?.departureTime,
        arrivalTime: state.currTrain?.arrivalTime
      },
      originalOrder: state.originalOrder
        ? {
            ridingDate: state.originalOrder.ridingDate,
            departureTime: state.originalOrder.departureTime,
            departure: state.originalOrder.departure,
            arrival: state.originalOrder.arrival,
            trainNumber: state.originalOrder.trainNumber,
            amountPerPassengerYuan: (() => {
              const ids = new Set(state.dataSource.map((p) => p.id))
              const matched = (state.originalOrder.passengerDetails || []).filter(
                (p) => ids.has(p.id)
              )
              const list = matched.length
                ? matched
                : state.originalOrder.passengerDetails || []
              if (!list.length) return 0
              const sum = list.reduce((s, p) => s + (p.amount || 0), 0)
              return sum / 100 / list.length
            })()
          }
        : null,
      passengers: state.dataSource.map((item, index) => {
        const origDetail = (state.originalOrder.passengerDetails || []).find(
          (p) => p.id === item.id
        )
        return {
          keyNumber: index + 1,
          id: item.id,
          realName: item.realName,
          idType: item.idType,
          idCard: item.idCard,
          discountType: item.discountType,
          seatType: item.seatType,
          originalAmount: origDetail?.amount ?? null,
          amount: state.currentSeat?.find((s) => s.type === item.seatType)?.price
            ? Math.round(
                state.currentSeat.find((s) => s.type === item.seatType).price * 100
              )
            : null
        }
      })
    })
    router.push('/rescheduleConfirm')
    return
  } else {
    const seatType = state.dataSource[0]?.seatType
    const seatInfo = state.currentSeat?.find((s) => s.type === seatType)
    params.quoteSnapshot = {
      timestamp: state.quoteTimestamp || query.quoteTimestamp || null,
      deliveryId: seatInfo?.deliveryId || null
    }
    // 正常购票流程
    fetchBuyTicket(params)
      .then((res) => {
        if (res.success) {
          message.success('订单提交成功，请在30分钟内完成网上支付')
          setTimeout(() => {
            router.push(`/order?sn=${res.data.orderSn}`)
          }, 500)
        } else {
          message.error(res.message)
        }
        state.loading = false
      })
      .catch((error) => {
        state.loading = false
        console.log(error)
      })
  }
}
</script>

<style lang="scss" scoped>
.buy-ticket-wrapper {
  max-width: 1320px;
  margin: 0 auto;

  .title-wrapper {
    display: flex;
    align-items: baseline;
    gap: 8px;
    min-height: 28px;
    h1 {
      font-size: 18px;
      margin: 0;
      color: #0f172a;
    }
    h2 {
      font-size: 13px;
      margin: 0;
      color: #64748b;
      font-weight: 400;
    }
  }
  .important-text {
    font-size: 16px;
    font-weight: bolder;
    color: #0f172a;
  }
  .seat-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 14px;
    > div {
      padding: 6px 10px;
      border-radius: 8px;
      border: 1px solid #e5edf8;
      background: #f8fbff;
      .price {
        color: #f97316;
        font-weight: 600;
      }
    }
  }
  .tip {
    color: #1d4ed8;
    background: #eef5ff;
    border-left: 3px solid #3b82f6;
    padding: 8px 10px;
    border-radius: 8px;
  }
  .user-tip {
    margin-bottom: 8px;
    .anticon-user {
      color: #2563eb;
      font-size: 20px;
      margin-right: 5px;
    }
  }
  .check-wrapper {
    padding: 8px 12px;
    border: 1px solid #e8eef8;
    border-radius: 10px;
    background: #f8fbff;
    .passenger-operator {
      display: flex;
      gap: 10px;
      align-items: center;
    }
  }

  .origin-order-summary {
    margin-top: 8px;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid #fed7aa;
    background: #fff7ed;
    font-size: 13px;
    line-height: 1.6;

    .origin-label {
      color: #9a3412;
      font-weight: 600;
    }

    .origin-value {
      color: #334155;
    }
  }

  .origin-ticket-cell {
    font-size: 12px;
    line-height: 1.7;
    color: #334155;

    .cell-label {
      display: inline-block;
      min-width: 2.5em;
      color: #94a3b8;
      margin-right: 4px;
    }

    .origin-price {
      color: #ea580c;
      font-weight: 600;
    }
  }
  .submit-btn {
    min-width: 112px;
    height: 36px;
    background: linear-gradient(135deg, #fb923c 0%, #f97316 100%);
    border: none;
    color: #fff;
    border-radius: 8px;
    box-shadow: 0 10px 18px rgba(249, 115, 22, 0.3);
  }
  .tips-txt {
    background: linear-gradient(180deg, #fffdf4 0%, #fff8e5 100%);
    border: 1px solid #ffe08a;
    border-radius: 12px;
    padding: 12px 14px;
  }
}

.buy-steps {
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
}

.buy-step {
  font-size: 12px;
  color: #64748b;
  border: 1px solid #dbe6f6;
  background: #f8fbff;
  border-radius: 999px;
  padding: 6px 12px;
}

.buy-step.done {
  color: #166534;
  border-color: #bbf7d0;
  background: #f0fdf4;
}

.buy-step.active {
  color: #1d4ed8;
  border-color: #bfdbfe;
  background: #eff6ff;
  font-weight: 600;
}

::v-deep {
  .ant-card-body {
    background: #ffffff;
    padding: 16px 18px;
  }
  .ant-divider-dashed {
    border-color: #dde6f3;
  }
  .ant-divider-horizontal {
    margin: 12px 0;
  }
  .ant-table-thead {
    .ant-table-cell {
      background: #f3f3f3 !important;
      background-color: #f3f3f3 !important;
    }
    > tr > th {
      padding: 8px;
    }
  }
  .check-info-wrapper {
    .ant-modal {
      top: 48px;
      padding-bottom: 24px;
    }
    .ant-modal-header {
      background: linear-gradient(180deg, #f8fbff 0%, #eef5ff 100%);
      padding: 12px 20px;
    }
    .ant-modal-body {
      padding: 14px 20px 18px;
    }
    .ant-modal-title {
      font-size: 16px;
    }
    .ant-divider-horizontal {
      margin: 8px 0;
    }
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 6px 8px;
    }
  }
}
.check-info-wrapper {
  .important-text {
    font-size: 16px;
    font-weight: bolder;
  }
  .small-text {
    font-size: 13px;
  }
  .info-tip {
    color: #909090;
    font-size: 13px;
    line-height: 1.5;
    span {
      font-size: 15px;
      color: #dc2626;
      font-weight: bolder;
    }
  }

  .seat-diagram-block {
    width: 100%;
  }

  .seat-disclaimer {
    display: block;
    color: #1677ff;
    font-size: 12px;
    line-height: 1.5;
    margin-bottom: 8px;
  }

  /* 卧铺选铺（参照 12306） */
  .berth-picker-block {
    width: 100%;
  }

  .berth-disclaimer {
    color: #1677ff;
    font-size: 13px;
    line-height: 1.6;
    margin-bottom: 12px;
  }

  .berth-choose-wrapper {
    width: 100%;
    background: #f8fbff;
    border: 1px solid #e4ecf8;
    border-radius: 12px;
    padding: 14px 16px;
  }

  .berth-choose-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 14px;
  }

  .berth-tip {
    color: #ea580c;
    background: transparent;
    border: none;
    padding: 0;
    margin-right: 0;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .berth-count-text {
    font-size: 14px;
    color: #334155;
  }

  .berth-steppers {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 20px 28px;
  }

  .berth-stepper-item {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .berth-label {
    min-width: 40px;
    font-size: 14px;
    color: #1e293b;
  }

  .berth-controls {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .berth-num {
    min-width: 24px;
    text-align: center;
    font-weight: 600;
    font-size: 15px;
    color: #0f172a;
  }

  .berth-step-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
}
.seat-choose-wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  background: #f8fbff;
  border: 1px solid #e4ecf8;
  border-radius: 10px;
  padding: 10px 14px;

  .seat-choose-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .seat-tip {
    color: #ea580c;
    background: transparent;
    border: none;
    padding: 0;
    margin: 0;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
  }

  .seat-count-text {
    font-size: 13px;
    color: #334155;
    white-space: nowrap;
  }

  .seat-rows {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 10px 20px;
  }

  .action-wrapper {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    background: #fff;
    border: 1px dashed #d8e3f3;
    border-radius: 8px;
    .seat-img {
      display: inline-block;
      text-align: center;
      width: 30px;
      height: 28px;
      line-height: 25px;
      background: url(https://kyfw.12306.cn/otn/resources/images/bg017.png)
        no-repeat;
      color: #fff;
      margin: 0 5px;
      cursor: pointer;
      background-position: -80px 0;
    }
    .cur {
      background-position: -40px 0;
    }
  }
}
</style>
